/**
 * DividerExamples.tsx
 * Comprehensive examples of HeroUI Divider component
 * 
 * Showcases all HeroUI Divider features using our shared components
 */

import React from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Divider } from '@enfomentis/ui';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';


export const DividerExamples: React.FC = () => {
  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Usage */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Usage</h3>
          <Card>
            <CardBody>
              <div className="space-y-4">
                <p className="text-text-primary">Content above the divider</p>
                <Divider />
                <p className="text-text-primary">Content below the divider</p>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Horizontal vs Vertical */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Orientations</h3>
          
          {/* Horizontal Divider */}
          <div className="mb-6">
            <h4 className="text-lg font-medium text-text-primary mb-3">Horizontal (Default)</h4>
            <Card>
              <CardBody>
                <div className="space-y-4">
                  <p className="text-text-primary">Section One</p>
                  <Divider />
                  <p className="text-text-primary">Section Two</p>
                  <Divider />
                  <p className="text-text-primary">Section Three</p>
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Vertical Divider */}
          <div>
            <h4 className="text-lg font-medium text-text-primary mb-3">Vertical</h4>
            <Card>
              <CardBody>
                <div className="flex items-center gap-4 h-20">
                  <div className="text-text-primary">Left Content</div>
                  <Divider orientation="vertical" />
                  <div className="text-text-primary">Middle Content</div>
                  <Divider orientation="vertical" />
                  <div className="text-text-primary">Right Content</div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Spacing Variations */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Custom Spacing</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <p className="text-text-primary">Content with default spacing</p>
                <Divider />
                
                <p className="text-text-primary">Content with tight spacing</p>
                <Divider className="my-2" />
                
                <p className="text-text-primary">Content with loose spacing</p>
                <Divider className="my-8" />
                
                <p className="text-text-primary">Final content</p>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Content Sections */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Section Separators</h3>
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">User Profile</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div>
                    <h5 className="font-medium text-text-primary mb-2">Personal Information</h5>
                    <p className="text-text-secondary">Name, email, and contact details</p>
                  </div>
                  
                  <Divider />
                  
                  <div>
                    <h5 className="font-medium text-text-primary mb-2">Account Settings</h5>
                    <p className="text-text-secondary">Privacy, notifications, and preferences</p>
                  </div>
                  
                  <Divider />
                  
                  <div>
                    <h5 className="font-medium text-text-primary mb-2">Security</h5>
                    <p className="text-text-secondary">Password, two-factor authentication, and login history</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Navigation/Menu Style */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Navigation Lists</h3>
          <Card>
            <CardBody>
              <div className="space-y-1">
                <div className="p-3 hover:bg-hover rounded cursor-pointer">
                  <span className="text-text-primary">Dashboard</span>
                </div>
                <Divider className="my-1" />
                
                <div className="p-3 hover:bg-hover rounded cursor-pointer">
                  <span className="text-text-primary">Analytics</span>
                </div>
                <Divider className="my-1" />
                
                <div className="p-3 hover:bg-hover rounded cursor-pointer">
                  <span className="text-text-primary">Settings</span>
                </div>
                <Divider className="my-1" />
                
                <div className="p-3 hover:bg-hover rounded cursor-pointer">
                  <span className="text-text-primary">Profile</span>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Layout Compositions */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Layout Compositions</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Left Column */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Trading Stats</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Total Trades</span>
                    <span className="text-text-primary font-medium">127</span>
                  </div>
                  <Divider />
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Win Rate</span>
                    <span className="text-success font-medium">68.5%</span>
                  </div>
                  <Divider />
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Total P&L</span>
                    <span className="text-profit font-medium">+$4,267</span>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Right Column with Vertical Divider */}
            <Card>
              <CardBody>
                <div className="flex h-32">
                  <div className="flex-1 flex flex-col justify-center items-center">
                    <div className="text-2xl font-bold text-text-primary">23</div>
                    <div className="text-sm text-text-secondary">Active Positions</div>
                  </div>
                  
                  <Divider orientation="vertical" className="mx-4" />
                  
                  <div className="flex-1 flex flex-col justify-center items-center">
                    <div className="text-2xl font-bold text-success">15</div>
                    <div className="text-sm text-text-secondary">Winning</div>
                  </div>
                  
                  <Divider orientation="vertical" className="mx-4" />
                  
                  <div className="flex-1 flex flex-col justify-center items-center">
                    <div className="text-2xl font-bold text-loss">8</div>
                    <div className="text-sm text-text-secondary">Losing</div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

      </div>
    </PlaygroundPageLayout>
  );
};